import { useCallback } from 'react';

import { useRowStoreHolding } from 'flow/components/nav/list-keys/row-index-store';
import useRowSelection from 'flow/components/nav/list-keys/useRowSelection';
import { ContactListContact } from 'flow/selectors/contact-list-selectors';
import { isEmptyNullOrUndefined } from 'flow/utils';
import {
    CENTRAL_SWITCH_OVERVIEW_KEY,
    ContactListRow,
} from './contact-list-rows';

export type OnItemSelected = (
    key: string,
    contact?: ContactListContact,
    status?: string,
) => void;

interface Options {
    rows: ContactListRow[];
    /** One key for each row, from uniqueRowKeys. */
    rowKeys: string[];
    selected?: string;
    searchText: string;
    onItemSelected: OnItemSelected;
    selectionFollowsFocus: boolean;
}

const rowKey = (row: ContactListRow) => {
    switch (row.kind) {
        case 'extension':
        case 'externalContact':
        case 'thirdPartyContact':
            return row.entityKey;
        case 'unknownKey':
            return row.key;
        case 'centralSwitchOverview':
            return CENTRAL_SWITCH_OVERVIEW_KEY;
        default:
            return null;
    }
};

// The highlight belongs to a row, not to a position: the transfer shortcuts
// act on it. The list shares its rule with the PBX list (useRowSelection).
//
// Where the selection follows focus, the highlight is the contact with the
// visible focus ring. When no contact has it, the highlight goes back to the
// row of the page that is open, which is grey, so a transfer never goes to a
// contact that the user cannot see as its target. In a picker the highlight
// is the Accept target, which only Enter or a click sets.
const useContactListSelection = ({
    rows,
    rowKeys,
    selected,
    searchText,
    onItemSelected,
    selectionFollowsFocus,
}: Options) => {
    // A favourite is in the list twice; the first one wins.
    const selectedIndex = selected
        ? rows.findIndex((row) => rowKey(row) === selected)
        : -1;
    const noChoiceIndex = useCallback(
        () =>
            !selectionFollowsFocus && !isEmptyNullOrUndefined(searchText)
                ? -1
                : selectedIndex,
        [selectedIndex, searchText, selectionFollowsFocus],
    );

    const {
        selectionStore,
        findChosenIndex,
        findSelectedIndex,
        chooseRow,
        dropChoice,
    } = useRowSelection({
        rowKeys,
        // Where the selection follows focus, another page that opens, as
        // by Back or a notification, leaves the choice alone: the choice
        // is the contact with the focus ring, which keeps the ring.
        openItemKey: selectionFollowsFocus ? undefined : selected,
        noChoiceIndex,
        // Where the selection follows focus, a new search text drops the
        // choice, so the highlight goes back to the open page's row.
        choiceScope: selectionFollowsFocus ? searchText : undefined,
    });

    // Focus without a ring, as after a click on a contact's button, and focus
    // on a row that is not a contact, give the highlight back to the open
    // page's row. Only Enter or a click opens a card.
    const onRowFocus = useCallback(
        (index: number, focusIsVisible: boolean) => {
            const row = rows[index];
            if (!row) {
                return;
            }
            const rowIsContact =
                row.kind !== 'sectionTitle' &&
                row.kind !== 'centralSwitchOverview';
            if (rowIsContact && focusIsVisible) {
                chooseRow(index, rowKey(row));
            } else {
                dropChoice();
            }
        },
        [rows, chooseRow, dropChoice],
    );

    const onRowClick = useCallback(
        (
            index: number,
            key: string,
            contact?: ContactListContact,
            status?: string,
        ) => {
            if (onItemSelected) {
                onItemSelected(key, contact, status);
                // Where the selection follows focus, a click shows no ring,
                // and the page it opens makes the contact the target anyway.
                // A choice here would outlast the next page that opens.
                if (!selectionFollowsFocus) {
                    chooseRow(index, key);
                }
            }
        },
        [onItemSelected, chooseRow, selectionFollowsFocus],
    );

    // The row of the page that is open stays grey while the highlight
    // follows focus. A picker has no open page.
    const openRowKey =
        selectionFollowsFocus && selectedIndex !== -1
            ? rowKeys[selectedIndex]
            : null;
    const openRowStore = useRowStoreHolding(openRowKey);

    // Focus enters the list at the contact the user chose. Without a choice
    // it enters at the first search result, not the open contact further
    // down, and without a search at the open contact; -1 means the first row.
    const findEntryIndex = useCallback(() => {
        const chosenIndex = findChosenIndex();
        if (chosenIndex !== -1 || !isEmptyNullOrUndefined(searchText)) {
            return chosenIndex;
        }
        return findSelectedIndex();
    }, [findChosenIndex, findSelectedIndex, searchText]);

    return {
        selectionStore,
        openRowStore,
        findEntryIndex,
        onRowClick,
        onRowFocus: selectionFollowsFocus ? onRowFocus : undefined,
        // Focus that leaves the list takes the highlight off the contact
        // with it.
        onFocusLeave: selectionFollowsFocus ? dropChoice : undefined,
    };
};

export default useContactListSelection;
